// ===================================
// ADMIN PROMPTS PAGE - STAGE 5
// src/pages/AdminPrompts.js
// Manage interview prompts
// ===================================

import React, { useState, useEffect, useCallback } from 'react';
import { getPrompts, createPrompt, updatePrompt, deletePrompt, duplicatePrompt, getPromptTypes } from '../services/promptApi';
import { getCategories } from '../services/categoryApi';
import './AdminPrompts.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AdminPrompts = () => {
  // State
  const [prompts, setPrompts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [promptTypes, setPromptTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);

  // Filters
  const [filters, setFilters] = useState({
    categoryId: '',
    promptType: '',
    personality: '',
    experienceLevel: '',
    isActive: ''
  });

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [formData, setFormData] = useState({
    category_id: '',
    subcategory_id: '',
    prompt_type: 'system',
    personality: '',
    experience_level: '',
    prompt_template: '',
    is_active: true
  });

  // Preview modal
  const [showPreview, setShowPreview] = useState(false);
  const [previewPrompt, setPreviewPrompt] = useState(null);

  // Load initial data
  useEffect(() => {
    loadCategories();
    loadPromptTypes();
  }, []);

  // Load prompts when filters change
  useEffect(() => {
    loadPrompts();
  }, [filters]);

  const loadCategories = async () => {
    try {
      const response = await getCategories();
      setCategories(response.categories || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadPromptTypes = async () => {
    try {
      const response = await getPromptTypes();
      setPromptTypes(response.types || []);
    } catch (err) {
      console.error('Failed to load prompt types:', err);
    }
  };

  const loadSubcategories = async (categoryId) => {
    if (!categoryId) {
      setSubcategories([]);
      return;
    }
    
    try {
      const category = categories.find(c => c.id === categoryId);
      if (category) {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/categories/${category.slug}/subcategories`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setSubcategories(data.subcategories || []);
      }
    } catch (err) {
      console.error('Failed to load subcategories:', err);
      setSubcategories([]);
    }
  };

  const loadPrompts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getPrompts({
        categoryId: filters.categoryId || undefined,
        promptType: filters.promptType || undefined,
        personality: filters.personality || undefined,
        experienceLevel: filters.experienceLevel || undefined,
        isActive: filters.isActive || undefined,
        limit: 100
      });
      setPrompts(response.prompts || []);
      setTotal(response.total || 0);
    } catch (err) {
      setError('Failed to load prompts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Handle category change in form
  const handleCategoryChange = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      category_id: categoryId,
      subcategory_id: ''
    }));
    loadSubcategories(categoryId);
  };

  // Open create modal
  const handleCreate = () => {
    setEditingPrompt(null);
    setFormData({
      category_id: '',
      subcategory_id: '',
      prompt_type: 'system',
      personality: '',
      experience_level: '',
      prompt_template: '',
      is_active: true
    });
    setSubcategories([]);
    setShowModal(true);
  };

  // Open edit modal
  const handleEdit = (prompt) => {
    setEditingPrompt(prompt);
    setFormData({
      category_id: prompt.category_id || '',
      subcategory_id: prompt.subcategory_id || '',
      prompt_type: prompt.prompt_type,
      personality: prompt.personality || '',
      experience_level: prompt.experience_level || '',
      prompt_template: prompt.prompt_template,
      is_active: prompt.is_active
    });
    if (prompt.category_id) {
      loadSubcategories(prompt.category_id);
    }
    setShowModal(true);
  };

  // Save prompt
  const handleSave = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        ...formData,
        category_id: formData.category_id || null,
        subcategory_id: formData.subcategory_id || null,
        personality: formData.personality || null,
        experience_level: formData.experience_level || null
      };

      if (editingPrompt) {
        await updatePrompt(editingPrompt.id, data);
      } else {
        await createPrompt(data);
      }
      
      setShowModal(false);
      loadPrompts();
    } catch (err) {
      alert('Failed to save prompt: ' + (err.response?.data?.message || err.message));
    }
  };

  // Delete prompt
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this prompt?')) return;
    
    try {
      await deletePrompt(id, false);
      loadPrompts();
    } catch (err) {
      alert('Failed to delete prompt');
    }
  };

  // Duplicate prompt
  const handleDuplicate = async (prompt) => {
    try {
      await duplicatePrompt(prompt.id, {});
      loadPrompts();
    } catch (err) {
      alert('Failed to duplicate prompt');
    }
  };

  // View prompt preview
  const handlePreview = (prompt) => {
    setPreviewPrompt(prompt);
    setShowPreview(true);
  };

  // Get label for prompt type
  const getTypeLabel = (type) => {
    const found = promptTypes.find(t => t.value === type);
    return found ? found.value : type;
  };

  // Get badge color for type
  const getTypeBadgeClass = (type) => {
    const colors = {
      system: 'badge-primary',
      greeting: 'badge-success',
      questions: 'badge-warning',
      evaluation: 'badge-info',
      followup: 'badge-secondary',
      closing: 'badge-dark'
    };
    return colors[type] || 'badge-secondary';
  };

  if (loading && prompts.length === 0) {
    return (
      <div className="admin-prompts-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading prompts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-prompts-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Interview Prompts</h1>
          <p>Manage AI interviewer prompts and question banks</p>
        </div>
        <button className="btn btn-primary" onClick={handleCreate}>
          + Add Prompt
        </button>
      </div>

      {error && (
        <div className="error-banner">
          <span>⚠️</span> {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <label>Category</label>
          <select
            value={filters.categoryId}
            onChange={(e) => setFilters({...filters, categoryId: e.target.value})}
          >
            <option value="">All Categories</option>
            <option value="null">Generic (No Category)</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Type</label>
          <select
            value={filters.promptType}
            onChange={(e) => setFilters({...filters, promptType: e.target.value})}
          >
            <option value="">All Types</option>
            {promptTypes.map(type => (
              <option key={type.value} value={type.value}>{type.value}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Personality</label>
          <select
            value={filters.personality}
            onChange={(e) => setFilters({...filters, personality: e.target.value})}
          >
            <option value="">All Personalities</option>
            <option value="friendly">Friendly</option>
            <option value="strict">Strict</option>
            <option value="technical">Technical</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Level</label>
          <select
            value={filters.experienceLevel}
            onChange={(e) => setFilters({...filters, experienceLevel: e.target.value})}
          >
            <option value="">All Levels</option>
            <option value="entry">Entry</option>
            <option value="intermediate">Intermediate</option>
            <option value="senior">Senior</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Status</label>
          <select
            value={filters.isActive}
            onChange={(e) => setFilters({...filters, isActive: e.target.value})}
          >
            <option value="">All</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-bar">
        <span className="stat-item">
          <strong>{total}</strong> prompts total
        </span>
        <span className="stat-item">
          <strong>{prompts.filter(p => p.is_active).length}</strong> active
        </span>
      </div>

      {/* Prompts Table */}
      <div className="prompts-table-container">
        <table className="prompts-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Category</th>
              <th>Personality</th>
              <th>Level</th>
              <th>Preview</th>
              <th>Version</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {prompts.map(prompt => (
              <tr key={prompt.id} className={!prompt.is_active ? 'inactive' : ''}>
                <td>
                  <span className={`badge ${getTypeBadgeClass(prompt.prompt_type)}`}>
                    {prompt.prompt_type}
                  </span>
                </td>
                <td>
                  <div className="category-cell">
                    <span className="category-name">
                      {prompt.category_name || 'Generic'}
                    </span>
                    {prompt.subcategory_name && (
                      <span className="subcategory-name">
                        → {prompt.subcategory_name}
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  {prompt.personality ? (
                    <span className="personality-badge">{prompt.personality}</span>
                  ) : (
                    <span className="any-badge">Any</span>
                  )}
                </td>
                <td>
                  {prompt.experience_level ? (
                    <span className="level-badge">{prompt.experience_level}</span>
                  ) : (
                    <span className="any-badge">Any</span>
                  )}
                </td>
                <td>
                  <div className="preview-text">
                    {prompt.prompt_template.substring(0, 80)}...
                  </div>
                </td>
                <td>
                  <span className="version-badge">v{prompt.version}</span>
                </td>
                <td>
                  <span className={`status-badge ${prompt.is_active ? 'active' : 'inactive'}`}>
                    {prompt.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-icon"
                      onClick={() => handlePreview(prompt)}
                      title="View"
                    >
                      👁️
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleEdit(prompt)}
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleDuplicate(prompt)}
                      title="Duplicate"
                    >
                      📋
                    </button>
                    <button
                      className="btn-icon danger"
                      onClick={() => handleDelete(prompt.id)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {prompts.length === 0 && (
          <div className="empty-state">
            <p>No prompts found matching your filters.</p>
            <button className="btn btn-primary" onClick={handleCreate}>
              Create First Prompt
            </button>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingPrompt ? 'Edit Prompt' : 'Create Prompt'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Prompt Type *</label>
                    <select
                      value={formData.prompt_type}
                      onChange={(e) => setFormData({...formData, prompt_type: e.target.value})}
                      required
                    >
                      {promptTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.value} - {type.description}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Category</label>
                    <select
                      value={formData.category_id}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                    >
                      <option value="">Generic (applies to all)</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Subcategory</label>
                    <select
                      value={formData.subcategory_id}
                      onChange={(e) => setFormData({...formData, subcategory_id: e.target.value})}
                      disabled={!formData.category_id || subcategories.length === 0}
                    >
                      <option value="">Any subcategory</option>
                      {subcategories.map(sub => (
                        <option key={sub.id} value={sub.id}>{sub.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Personality</label>
                    <select
                      value={formData.personality}
                      onChange={(e) => setFormData({...formData, personality: e.target.value})}
                    >
                      <option value="">Any personality</option>
                      <option value="friendly">Friendly</option>
                      <option value="strict">Strict</option>
                      <option value="technical">Technical</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Experience Level</label>
                    <select
                      value={formData.experience_level}
                      onChange={(e) => setFormData({...formData, experience_level: e.target.value})}
                    >
                      <option value="">Any level</option>
                      <option value="entry">Entry Level</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="senior">Senior</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Prompt Template *</label>
                  <textarea
                    value={formData.prompt_template}
                    onChange={(e) => setFormData({...formData, prompt_template: e.target.value})}
                    placeholder="Enter the prompt template...

Available variables:
{{DURATION}} - Session duration in minutes
{{QUESTION_COUNT}} - Number of questions
{{PERSONALITY}} - Interviewer personality
{{EXPERIENCE_LEVEL}} - Candidate level
{{COMPANY_NAME}} - Target company name
{{CATEGORY}} - Interview category"
                    rows={15}
                    required
                  />
                  <small>{formData.prompt_template.length} characters</small>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingPrompt ? 'Update Prompt' : 'Create Prompt'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && previewPrompt && (
        <div className="modal-overlay" onClick={() => setShowPreview(false)}>
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Prompt Preview</h2>
              <button className="modal-close" onClick={() => setShowPreview(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="preview-meta">
                <span className={`badge ${getTypeBadgeClass(previewPrompt.prompt_type)}`}>
                  {previewPrompt.prompt_type}
                </span>
                {previewPrompt.category_name && (
                  <span className="meta-item">📁 {previewPrompt.category_name}</span>
                )}
                {previewPrompt.personality && (
                  <span className="meta-item">🎭 {previewPrompt.personality}</span>
                )}
                {previewPrompt.experience_level && (
                  <span className="meta-item">📊 {previewPrompt.experience_level}</span>
                )}
                <span className="meta-item">v{previewPrompt.version}</span>
              </div>

              <div className="preview-content">
                <pre>{previewPrompt.prompt_template}</pre>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowPreview(false)}>
                Close
              </button>
              <button className="btn btn-primary" onClick={() => {
                setShowPreview(false);
                handleEdit(previewPrompt);
              }}>
                Edit Prompt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPrompts;
